import { countries as defaultCountries } from 'countries-list';
import cn from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import useOnClickOutside from '~/hooks/on-click-outside';
import { CountyRecord } from '~/interfaces/city';
import ReactCountryFlag from 'react-country-flag';
type SelectParam = {
   code: string;
   phone: string;
};

type PhonePickerProps = React.HTMLAttributes<HTMLInputElement> & {
   // eslint-disable-next-line no-unused-vars
   onCountrySelected?: (params: SelectParam) => any;
   showTop?: boolean;
   countries?: CountyRecord;
   value?: any;
   placeholder?: string;
};
const PhonePicker = ({
   className,
   showTop,
   onCountrySelected,
   countries,
   ...props
}: PhonePickerProps) => {
   const [open, setOpen] = useState(false);
   const [search, setSearch] = useState('');
   const [countryCode, setCountryCode] = useState('');
   const ref = useRef(null);
   const inputRef = useRef<HTMLInputElement>(null);
   const firstItemRef = useRef<HTMLLIElement>(null);
   useOnClickOutside(ref, () => setOpen(false));

   const list = useMemo(() => {
      const s = search.toLowerCase();
      return Object.entries(countries ? countries : defaultCountries).filter(([code, country]) => {
         const phone = `( +${country.phone.toLocaleLowerCase()} )`;
         return (
            (code.toLocaleLowerCase().includes(s) ||
               phone.includes(s) ||
               country.name.toLocaleLowerCase().includes(s)) &&
            code !== 'IL'
         );
      });
   }, [countries, search]);

   const itemClicked = ({
      e,
      code,
      phone,
   }: {
      e: React.MouseEvent<HTMLLIElement>;
      code: string;
      phone: string;
   }) => {
      e.stopPropagation();
      if (inputRef.current) inputRef.current.value = phone;
      setSearch('');
      setOpen(false);
      onCountrySelected && onCountrySelected({ code, phone });
   };

   const shiftSibling = (e: React.KeyboardEvent<HTMLLIElement>) => {
      const prevSibling = e.currentTarget.previousElementSibling as HTMLLIElement;
      const nextSibling = e.currentTarget.nextElementSibling as HTMLLIElement;
      if (e.key === 'ArrowUp' && prevSibling) prevSibling.focus();
      if (e.key === 'ArrowDown' && nextSibling) nextSibling.focus();
   };

   useEffect(() => {
      if (list?.length && props?.value) {
         const _code = props?.value?.startsWith?.('+') ? props?.value?.slice?.(1) : '';
         // eslint-disable-next-line @typescript-eslint/no-unused-vars
         const countryData: any = list.find(([_, { phone }]: any) => phone == _code);
         setCountryCode(countryData?.[0] || '');
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   return (
      <div
         onFocus={() => setOpen(true)}
         onClick={() => {
            setSearch('');
            if (inputRef.current) inputRef.current.value = '';
            setTimeout(() => {
               setCountryCode('');
               onCountrySelected?.({ code: '', phone: '' });
            }, 100);
            setOpen(true);
         }}
         ref={ref}
         className="h-full">
         <div className="flex items-center w-full h-full px-2 bg-white">
            <ReactCountryFlag
               countryCode={countryCode}
               key={countryCode}
               svg
               style={{
                  width: '1rem',
                  height: '1rem',
               }}
               title={countryCode}
               className="ltr:mr-2 rtl:ml-2"
            />
            <input
               onChange={e => setSearch(e.currentTarget.value ?? '')}
               autoCorrect="off"
               autoComplete="none"
               autoSave="off"
               defaultValue=""
               ref={inputRef}
               className={cn(
                  'w-full h-full focus:outline-gray-200 !border-none ring-0 focus:!outline-0 ',
                  className
               )}
               maxLength={3}
               {...props}
            />
         </div>
         {open && (
            <ul
               aria-haspopup="menu"
               aria-expanded={open ? 'true' : 'false'}
               role="menu"
               aria-labelledby="options-menu"
               className={cn(
                  'absolute z-40 max-w-full py-4 overflow-y-auto border rounded-xl bg-white rounded-b max-h-64 ltr:left-0 rtl:right-0 shadow-base',
                  showTop ? 'bottom-full' : 'top-full'
               )}>
               {list.map(([code, { phone, name }], idx) => (
                  <li
                     ref={idx === 0 ? firstItemRef : null}
                     key={code}
                     onKeyUp={shiftSibling}
                     className="px-4 py-2 text-sm hover:bg-gray-100 focus:bg-gray-100"
                     role="menuitem"
                     onClick={e => {
                        itemClicked({ e, phone, code });
                        setCountryCode(code);
                     }}
                     tabIndex={-1}>
                     <span>
                        <ReactCountryFlag
                           countryCode={code}
                           svg
                           style={{
                              width: '1rem',
                              height: '1rem',
                           }}
                           title={code}
                           className="ltr:mr-2 rtl:ml-2"
                        />
                        {name} ( +{phone} )
                     </span>
                  </li>
               ))}
            </ul>
         )}
      </div>
   );
};

export default PhonePicker;
